import { ActionMenu, ActionMenuItem, FormInput } from '@systemeio/ui'
import {
  CreateModuleErrorsInterface,
  CreateModuleInterface,
  ModuleId,
} from 'modules/courses/courses/curriculum/types/module-interface'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import { CourseId } from 'modules/courses/courses/types/course-interface'
import { memo, useState } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import EditModal from 'shared/components/edit-modal'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'
import { useModuleApi } from '../../curriculum/hooks/use-module-api'
import { useLightCourseActions } from '../hooks/use-light-course-actions'
import { LightCourseCurriculumInterface } from '../light-course-interface'

export type LightCourseModuleActionsProps = {
  module: LightCourseCurriculumInterface['modules'][number]
  mutate: KeyedMutator<LightCourseCurriculumInterface>
}

const defaultErrors: CreateModuleErrorsInterface = {
  fields: {
    name: '',
  },
}

export const LightCourseModuleActions = memo(function LightCourseModuleActions({
  module,
  mutate,
}: LightCourseModuleActionsProps) {
  const { t } = useLocoTranslation()

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isRemoveOpen, setIsRemoveOpen] = useState(false)
  const [editErrors, setEditErrors] = useState(defaultErrors)
  const caption = t('dashboard.course.module_title')
  const { removeModuleWithLectures } = useModuleApi()
  const { updateLightCourseModule } = useLightCourseActions()

  const { revalidateModules } = useRevalidateModules()

  const hasLectures = module.lectures?.length > 0

  const onRemoveModule = async (moduleId: CourseId) => {
    await removeModuleWithLectures(moduleId)

    await mutate(data => {
      if (data) {
        return {
          ...data,
          modules: data.modules.filter(module => module.id !== moduleId),
        }
      }
    }, false)

    await revalidateModules([
      RequestModuleEnum.MembershipLightCourseCurriculum,
      RequestModuleEnum.MembershipCourseLecture,
    ])
  }

  const onEditModule = async (moduleId: ModuleId, data: CreateModuleInterface) => {
    await fetchWithFieldErrors(async () => {
      const response = await updateLightCourseModule(moduleId, data)
      await mutate(data => {
        if (data) {
          return {
            ...data,
            modules: data.modules.map(module => {
              if (module.id === moduleId) {
                return response
              }
              return module
            }),
          }
        }
      }, false)
    }, setEditErrors)

    await revalidateModules([RequestModuleEnum.MembershipLightCourseCurriculum])
  }

  return (
    <div className={`flex items-center justify-end`}>
      <div {...nonDraggableProps}>
        <ActionMenu
          menuItems={[
            <ActionMenuItem
              key="module-settings"
              label={t('dashboard.actions.title.settings')}
              onClick={() => {
                setIsSettingsOpen(true)
              }}
            />,
            <ActionMenuItem
              key="module-remove"
              label={t('dashboard.actions.title.remove')}
              onClick={() => {
                setIsRemoveOpen(true)
              }}
            />,
          ]}
        />

        <EditModal
          fullCaption={t('dashboard.course.module_edit_settings_title')}
          opened={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false)
          }}
          instance={module}
          editable={[
            {
              field: 'name',
              onRender: (value, onChange) => (
                <FormInput
                  key="module-edit-name"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  label={t('global.name')}
                  error={editErrors.fields.name}
                />
              ),
            },
          ]}
          onEdit={data => onEditModule(module.id, data)}
        />
        <Confirmation
          opened={isRemoveOpen}
          onClose={() => setIsRemoveOpen(false)}
          onConfirm={() => onRemoveModule(module.id)}
          confirmationText={t(
            hasLectures
              ? 'dashboard.actions.remove_with_lectures_confirmation'
              : 'dashboard.actions.remove_confirmation',
            {
              module: caption?.toLowerCase(),
            },
          )}
          toastCaption={t('dashboard.actions.removed', { module: caption?.toLowerCase() })}
        />
      </div>
    </div>
  )
})
